import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/api';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  StatusIndicator,
  ColumnLayout,
  Alert,
  ExpandableSection,
  KeyValuePairs
} from '@cloudscape-design/components';
import { parseAiRecommendationPercentageScore } from '../../../common/utility';
import { submissionsApi } from '../../../api/Submission';
import { API } from '@aws-amplify/api';
import { GraphQLAuthMode } from '@aws-amplify/api-graphql';


const client = generateClient({
 
});

const SUBSCRIPTION = `
  subscription OnBroadcast($channelId: String!) {
    onBroadcast(channelId: $channelId) {
      id
      content
      timestamp
    }
  }
`;


const SEVERITY = {
  low: { rank: 0, type: 'error', label: 'Missing' },
  medium: { rank: 1, type: 'warning', label: 'Partial' }
};

const severityOf = (rec) => SEVERITY[String(rec?.score || '').toLowerCase()];

const countBySeverity = (recs) => {
  const missing = recs.filter(rec => severityOf(rec)?.label === 'Missing').length;
  const partial = recs.filter(rec => severityOf(rec)?.label === 'Partial').length;
  return { missing, partial };
};

const RecommendationsSection = ({ recommendations }) => {
  const formatRecommendations = (recs) => {
    return [...recs]
      .sort((a, b) => (severityOf(a)?.rank ?? 2) - (severityOf(b)?.rank ?? 2))
      .map(rec => {
        const severity = severityOf(rec);
        // Requirements with no evidence at all cost twice what a partial does,
        // so they stay open. Partials fold away to keep the list readable:
        // a typical indicator carries about a dozen findings.
        const analysis = severity && severity.rank > 0
          ? (
            <ExpandableSection headerText="Show detail" variant="footer">
              <div>{rec.analysis}</div>
            </ExpandableSection>
          )
          : <div>{rec.analysis}</div>;
        return {
          label: rec.point,
          value: (
            <SpaceBetween size="xxs">
              {severity && (
                <StatusIndicator type={severity.type}>{severity.label}</StatusIndicator>
              )}
              {analysis}
            </SpaceBetween>
          )
        };
      });
  };

  const allRecommendations = [
    ...(recommendations.description_recommendations || []),
    ...(recommendations.admin_recommendations || [])
  ];
  const { missing, partial } = countBySeverity(allRecommendations);

  return (recommendations.description_recommendations?.length > 0 ||
        recommendations.admin_recommendations?.length > 0) &&
      (
    
    <Container>
      <SpaceBetween size="l">
        {recommendations.description_recommendations?.length > 0 && (
          <>
            <Header
              variant="h2"
              counter={`(${allRecommendations.length})`}
              description={`${missing} with no evidence, ${partial} partially evidenced`}
            >
              AI Recommendations
            </Header>
            <KeyValuePairs
              items={formatRecommendations(recommendations.description_recommendations)}
              columns={1}
            />
          </>
        )}

        {recommendations.admin_recommendations?.length > 0 && (
          <>
            {/* <Header variant="h2">Administrative Recommendations</Header> */}
            <KeyValuePairs
              items={formatRecommendations(recommendations.admin_recommendations)}
              columns={1}
            />
          </>
        )}
      </SpaceBetween>
    </Container>
  );
};

const BroadcastSubscription = ({ 
  topicId, 
  initialScore = 0, 
  initialRecommendations = [] 
}) => {
  const [loadingStatus, setLoadingStatus] = useState('');
  const [score, setScore] = useState(initialScore);
  const [recommendations, setRecommendations] = useState(initialRecommendations);
  const [subscription, setSubscription] = useState(null);
  const [loadingText, setLoadingText] = useState('');
  const [error, setError] = useState(null);
  const token = localStorage.getItem('access_token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userId = user.id;
  const appendText = (newText) => {
    setLoadingText(prevText => prevText + newText);
  };

  const resetState = () => {
    setLoadingStatus('');
    setScore(0);
    setRecommendations([]);
    setLoadingText('');
    setError(null);
  };


  useEffect(() => {
    const sub = client.graphql({
      query: SUBSCRIPTION,
      variables: {
        channelId: userId,
      },
      authMode: 'lambda',
      authToken: token
    }).subscribe({
      next: ({ data }) => {
        const message = data.onBroadcast;
        const content = JSON.parse(message.content);

        if (content.fanout_topic_id !== topicId) {
          return;
        }
        // Handle different actions
        switch (content.action) {
          case 'START':
            resetState();
            return;
          case 'GENERAL_ERROR':
            resetState();
            setError('An error occurred in the AI processing engine. Please try again later.');
            return;
        }

        // Handle loading status updates
        if (content.loading_status) {
          setLoadingStatus(content.loading_status);
        }

        // Handle score updates
        if (content.score) {
          setScore(parseAiRecommendationPercentageScore(content.score));
        }

        if (content.loading_text) {
          appendText(content.loading_text);
        }
        if (content.recommendations) {
          setRecommendations(content.recommendations);
        }

        // The full recommendations payload is too large to broadcast (WAF blocks
        // request bodies over 8 KB), so the lambda sends a small "ready" signal
        // once the row is stored; re-fetch the recommendations from the DB here.
        if (content.recommendations_ready) {
          submissionsApi
            .getLatestAgentSubmissionRecommendations(topicId)
            .then((rec) => {
              if (!rec) return;
              if (rec.score) {
                setScore(parseAiRecommendationPercentageScore(rec.score));
              }
              setRecommendations(rec.details?.recommendations || []);
            })
            .catch((err) =>
              console.warn('Failed to fetch recommendations after ready signal:', err)
            );
        }

        // if (content.recommendations) {
        //   setRecommendations(content.recommendations);
        // }
        
      },
      error: error => console.warn('Subscription error:', error)
    });

    setSubscription(sub);

    // Cleanup subscription on unmount
    return () => {
      if (sub) {
        sub.unsubscribe();
      }
    };
  }, [topicId, initialScore, initialRecommendations]);

  // Update score and recommendations when initial values change
  useEffect(() => {
    setScore(initialScore);
    setRecommendations(initialRecommendations);
  }, [initialScore, initialRecommendations]);

  if (error) {
    
    return(     
    <div className="p-4 max-w-lg mx-auto">
    <h2 className="text-xl font-bold mb-4">Eduscribe AI Response</h2>
    
    <Alert type="error">{error}</Alert>
    </div>
    )
  }

  return (
    <div className="p-4 max-w-lg mx-auto">
      <h2 className="text-xl font-bold mb-4">Eduscribe AI Response</h2>
      
      {/* Score Display */}
      <div className="mb-4 p-4 bg-blue-50 rounded-lg">
        <h1 className="font-semibold mb-2">
          Readiness Score: {parseAiRecommendationPercentageScore(score)}%
        </h1>
        <p> 
        Disclaimer: This result is generated by artificial intelligence and is provided for guidance purposes only to assist institutions in checking the completeness and clarity of their applications. After the submission, the application will be evaluated and judged by the Institutional Listing Evaluation Panel. 
        </p>
      </div>

      {/* Loading Text Display */}
      {loadingText && (
        <div 
          className="mb-4 p-4 bg-white rounded-lg border border-gray-200 h-64 overflow-y-auto whitespace-pre-wrap"
        >
          {loadingText}
        </div>
      )}

      {/* Loading Status */}
      {loadingStatus && (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-gray-600">
            ◌ {loadingStatus}
          </p>
        </div>
      )}
  
      <RecommendationsSection recommendations={recommendations} />

      {/* Empty State */}
      {!loadingStatus && 
      !recommendations.description_recommendations?.length > 0 &&
      !recommendations.admin_recommendations?.length > 0 &&      
      (
        <p className="text-gray-500 text-center py-4">
          Waiting for analysis to begin...
        </p>
      )}
    </div>
  );
};

export default BroadcastSubscription;